import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  CircleArrowLeft,
  CircleArrowRight,
  ImageIcon,
  X,
} from 'lucide-react';
import Button from './Button';
import type { PurchaseProduct } from '../utils/marketplaceProducts';
import { formatMarketplaceProductPricing, toPurchaseProduct } from '../utils/marketplaceProducts';

export interface CoreOutput {
  name: string;
  text?: string;
  path: string;
  image_url?: string;
}

export interface OverviewProduct {
  id: number;
  name: string;
  product_category: number;
  credits: number;
  duration_in_days: number;
  short_description: string;
  long_description: string;
  service_type?: 'DIY' | 'AI' | 'AI Off-Platform' | 'Catalyx';
  product_category_workspace_key?: string;
  is_free?: boolean;
  core_outputs_json?: CoreOutput[];
}

export interface OverviewProductCategory {
  id: number;
  name: string;
  short_description: string;
  long_description: string;
  category_type_id: number;
  workspace_key?: string;
  image_url?: string | null;
}

interface ProductCategoryOverviewProps {
  category: OverviewProductCategory;
  products: OverviewProduct[];
  relatedCategories: OverviewProductCategory[];
  productsByCategory: Record<number, OverviewProduct[]>;
  color: string;
  getRelatedCategoryColor: (category: { id: number; category_type_id: number }) => string;
  onBack: () => void;
  onViewOrderHistory?: () => void;
  onPurchase: (product: PurchaseProduct) => void;
  canAiGenerate?: boolean;
  canMarketplaceOrder?: boolean;
  onSelectCategory: (categoryId: number) => void;
  initialProductId?: number | null;
}

function pickLowestCreditsProduct(products: OverviewProduct[]): OverviewProduct | null {
  if (products.length === 0) return null;
  return products.reduce((best, product) => {
    if (product.credits < best.credits) return product;
    if (product.credits === best.credits && product.duration_in_days < best.duration_in_days) {
      return product;
    }
    return best;
  });
}

function getDisplayProduct(
  products: OverviewProduct[],
  selectedProductId: number | null
): OverviewProduct | null {
  if (products.length === 0) return null;
  if (products.length === 1) return products[0];
  if (selectedProductId != null) {
    return products.find((product) => product.id === selectedProductId) ?? null;
  }
  return pickLowestCreditsProduct(products);
}

const selectClassName =
  'w-[239px] h-8 rounded-lg border border-[#E8E8E8] bg-white px-3 text-[14px] text-[#626262] focus:outline-none focus:ring-1 focus:ring-[#674fa3]';

const ProductCategoryOverview: React.FC<ProductCategoryOverviewProps> = ({
  category,
  products,
  relatedCategories,
  productsByCategory,
  color,
  getRelatedCategoryColor,
  onBack,
  onViewOrderHistory,
  onPurchase,
  canAiGenerate = true,
  canMarketplaceOrder = true,
  onSelectCategory,
  initialProductId = null,
}) => {
  const [manualProductIndex, setManualProductIndex] = useState<number | null>(null);
  const [outputPage, setOutputPage] = useState(0);
  const [enlargedOutputIndex, setEnlargedOutputIndex] = useState<number | null>(null);
  const [relatedSelectedProducts, setRelatedSelectedProducts] = useState<
    Record<number, number | null>
  >({});

  const initialProductIndex = useMemo(() => {
    if (initialProductId == null) return 0;
    const idx = products.findIndex((product) => product.id === initialProductId);
    return idx >= 0 ? idx : 0;
  }, [initialProductId, products]);

  const activeProductIndex = manualProductIndex ?? initialProductIndex;
  const activeProduct = products[activeProductIndex] ?? products[0] ?? null;
  const outputs = activeProduct?.core_outputs_json ?? [];
  const outputsPerPage = 3;
  const totalOutputPages = Math.max(1, Math.ceil(outputs.length / outputsPerPage));
  const visibleOutputs = outputs.slice(
    outputPage * outputsPerPage,
    outputPage * outputsPerPage + outputsPerPage
  );

  useEffect(() => {
    setManualProductIndex(null);
    setOutputPage(0);
    setEnlargedOutputIndex(null);
    setRelatedSelectedProducts({});
  }, [category.id, initialProductId]);

  useEffect(() => {
    setOutputPage(0);
    setEnlargedOutputIndex(null);
  }, [activeProduct?.id]);

  useEffect(() => {
    if (outputPage >= totalOutputPages) {
      setOutputPage(Math.max(0, totalOutputPages - 1));
    }
  }, [outputPage, totalOutputPages]);

  useEffect(() => {
    if (enlargedOutputIndex == null) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setEnlargedOutputIndex(null);
      } else if (event.key === 'ArrowLeft' && outputs.length > 0) {
        setEnlargedOutputIndex(
          (prev) => ((prev ?? 0) - 1 + outputs.length) % outputs.length
        );
      } else if (event.key === 'ArrowRight' && outputs.length > 0) {
        setEnlargedOutputIndex((prev) => ((prev ?? 0) + 1) % outputs.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enlargedOutputIndex, outputs.length]);

  const handleRelatedProductSelect = (categoryId: number, productId: string) => {
    setRelatedSelectedProducts((prev) => ({
      ...prev,
      [categoryId]: productId ? Number(productId) : null,
    }));
  };

  const renderRelatedCard = (relatedCategory: OverviewProductCategory) => {
    const categoryProducts = productsByCategory[relatedCategory.id] ?? [];
    const hasMultipleProducts = categoryProducts.length > 1;
    const selectedProductId = relatedSelectedProducts[relatedCategory.id] ?? null;
    const displayProduct = getDisplayProduct(categoryProducts, selectedProductId);

    const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement).closest('[data-card-interactive="true"]')) {
        return;
      }
      onSelectCategory(relatedCategory.id);
    };

    return (
      <div
        key={relatedCategory.id}
        className="w-[264px] flex-shrink-0 cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="w-[264px] h-[209px] rounded-t-lg border border-[#E8E8E8] bg-[#F9F9F9] overflow-hidden">
          {relatedCategory.image_url ? (
            <img
              src={relatedCategory.image_url}
              alt={relatedCategory.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-gray-300" strokeWidth={1.5} />
            </div>
          )}
        </div>

        <div
          className="w-[262px] mx-auto h-2"
          style={{ backgroundColor: getRelatedCategoryColor(relatedCategory) }}
        />

        <div className="w-[264px] min-h-[307px] rounded-b-lg border border-[#E8E8E8] border-t-0 bg-white px-3 py-3 flex flex-col">
          <h3 className="text-[18px] font-bold leading-none text-[#0C0B0B] mb-2">
            {relatedCategory.name}
          </h3>
          <p className="text-[14px] font-normal leading-normal text-[#626262] flex-1">
            {relatedCategory.short_description}
          </p>

          <div className="mt-auto flex flex-col gap-4 pt-4">
            {hasMultipleProducts && (
              <select
                data-card-interactive="true"
                value={selectedProductId ?? ''}
                onChange={(event) =>
                  handleRelatedProductSelect(relatedCategory.id, event.target.value)
                }
                className={selectClassName}
              >
                <option value="" disabled hidden>
                  Select an option
                </option>
                {categoryProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            )}

            {displayProduct && (
              <p className="text-[14px] font-bold leading-none text-[#6750A4]">
                {formatMarketplaceProductPricing(displayProduct)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const enlargedOutput =
    enlargedOutputIndex != null ? outputs[enlargedOutputIndex] ?? null : null;

  return (
    <div className="w-full min-h-full bg-white font-[Inter,sans-serif]">
      <div className="bg-[#F9F9F9]">
        <div className="px-8 pt-6 pb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-[14px] text-[#3ea2d3] hover:text-[#2d8ab8] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="underline">Back to marketplace</span>
          </button>
          {onViewOrderHistory && (
            <button
              type="button"
              onClick={onViewOrderHistory}
              className="shrink-0 px-5 py-2 rounded-full border-2 border-[#674fa3] text-[#674fa3] text-[14px] font-bold hover:bg-[#674fa3]/5 transition-colors"
            >
              View order history
            </button>
          )}
        </div>

        <div className="px-8 max-w-[1128px]">
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="flex-1 min-w-0">
              <h1 className="text-[24px] font-bold leading-none text-[#0C0B0B] mb-4">
                {category.name}
              </h1>
              {category.long_description && (
                <p className="text-[14px] font-normal leading-normal text-[#626262] whitespace-pre-line">
                  {category.long_description}
                </p>
              )}
            </div>

            <div className="w-full lg:w-[517px] flex-shrink-0">
              <div className="w-full rounded-lg border border-[#E8E8E8] overflow-hidden">
                <div className="w-full h-[257px] bg-[#F9F9F9] overflow-hidden">
                  {category.image_url ? (
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-10 h-10 text-gray-300" strokeWidth={1.5} />
                    </div>
                  )}
                </div>
                <div className="w-full h-2" style={{ backgroundColor: color }} />
              </div>
            </div>
          </div>

          {products.length > 0 && (
            <section>
              <h2 className="text-[24px] font-bold leading-none text-[#0C0B0B] mb-4">
                Purchase Options
              </h2>

              <div className="flex flex-wrap items-end gap-2 border-b border-[#D1D1D1]">
                {products.map((product, index) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => setManualProductIndex(index)}
                    className={`text-[14px] px-4 py-2.5 transition-colors ${
                      activeProductIndex === index
                        ? 'rounded-t-lg border border-[#D1D1D1] border-b-white bg-white text-[#6750A4] font-bold -mb-px z-10'
                        : 'border border-transparent text-[#626262] font-bold'
                    }`}
                  >
                    {product.name}
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      <div className="px-8 pt-6 pb-10 max-w-[1128px]">
        {activeProduct && products.length > 0 && (
          <div className="mb-10">
            {activeProduct.short_description && (
              <p className="text-[14px] font-normal leading-normal text-[#626262] mb-4 max-w-[864px]">
                {activeProduct.short_description}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4">
              <p className="text-[14px] font-bold leading-none text-[#6750A4]">
                {formatMarketplaceProductPricing(activeProduct)}
              </p>
              <div
                title={
                  activeProduct.service_type === 'AI' && !canAiGenerate
                    ? 'You do not have permission to order AI products'
                    : (activeProduct.service_type === 'Catalyx' ||
                          activeProduct.service_type === 'AI Off-Platform') &&
                        !canMarketplaceOrder
                      ? 'You do not have permission to order Catalyx marketplace products'
                      : undefined
                }
              >
                <Button
                  label="Purchase"
                  variant="Round"
                  size="Small"
                  className="w-[239px]"
                  disabled={false}
                  state="Enabled"
                  onClick={() =>
                    onPurchase(
                      toPurchaseProduct(activeProduct, { workspaceKey: category.workspace_key })
                    )
                  }
                />
              </div>
            </div>
          </div>
        )}

        {outputs.length > 0 && (
          <section className="mb-10 pt-6 border-t border-[#E8E8E8]">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[24px] font-bold leading-none text-[#0C0B0B]">Output Examples</h2>
              {outputs.length > outputsPerPage && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setOutputPage((prev) => Math.max(0, prev - 1))}
                    disabled={outputPage === 0}
                    className="text-[#626262] disabled:opacity-40 disabled:cursor-not-allowed hover:text-[#0C0B0B] transition-colors"
                    aria-label="Previous outputs"
                  >
                    <CircleArrowLeft className="w-6 h-6" />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setOutputPage((prev) => Math.min(totalOutputPages - 1, prev + 1))
                    }
                    disabled={outputPage >= totalOutputPages - 1}
                    className="text-[#626262] disabled:opacity-40 disabled:cursor-not-allowed hover:text-[#0C0B0B] transition-colors"
                    aria-label="Next outputs"
                  >
                    <CircleArrowRight className="w-6 h-6" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-6">
              {visibleOutputs.map((output, index) => {
                const globalIndex = outputPage * outputsPerPage + index;
                return (
                  <div
                    key={`${output.name}-${globalIndex}`}
                    className="w-[264px] flex-shrink-0 flex flex-col"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (output.image_url) {
                          setEnlargedOutputIndex(globalIndex);
                        }
                      }}
                      className="w-[264px] h-[209px] rounded-t-lg border border-[#E8E8E8] bg-[#F9F9F9] overflow-hidden block"
                      disabled={!output.image_url}
                    >
                      {output.image_url ? (
                        <img
                          src={output.image_url}
                          alt={output.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-300" strokeWidth={1.5} />
                        </div>
                      )}
                    </button>

                    <div className="w-[264px] flex-1 rounded-b-lg border border-[#E8E8E8] border-t-0 bg-white px-3 py-4 flex flex-col">
                      <h3 className="text-[18px] font-bold leading-none text-[#0C0B0B] mb-2">
                        {output.name}
                      </h3>
                      <p className="text-[14px] font-normal leading-normal text-[#626262]">
                        {output.text || output.path}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {relatedCategories.length > 0 && (
          <section className="pt-6 border-t border-[#E8E8E8]">
            <h2 className="text-[24px] font-bold leading-none text-[#0C0B0B] mb-6">Related Services</h2>
            <div className="flex flex-wrap gap-6">
              {relatedCategories.map((relatedCategory) => renderRelatedCard(relatedCategory))}
            </div>
          </section>
        )}
      </div>

      {enlargedOutput?.image_url && enlargedOutputIndex != null && (
        <div
          className="fixed inset-0 z-[15500] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          onClick={() => setEnlargedOutputIndex(null)}
        >
          <div
            className="w-full max-w-[900px] max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between px-8 pt-8 pb-4">
              <div className="min-w-0 pr-4">
                <h2 className="text-[20px] font-bold text-[#0C0B0B]">{enlargedOutput.name}</h2>
                {(enlargedOutput.text || enlargedOutput.path) && (
                  <p className="mt-3 text-[14px] font-normal leading-normal text-[#626262]">
                    {enlargedOutput.text || enlargedOutput.path}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setEnlargedOutputIndex(null)}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-8 pb-8 relative">
              {outputs.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setEnlargedOutputIndex(
                        (enlargedOutputIndex - 1 + outputs.length) % outputs.length
                      )
                    }
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 border border-[#E8E8E8] shadow-sm hover:bg-gray-50 transition-colors"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5 text-[#626262]" />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setEnlargedOutputIndex((enlargedOutputIndex + 1) % outputs.length)
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 border border-[#E8E8E8] shadow-sm hover:bg-gray-50 transition-colors"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5 text-[#626262]" />
                  </button>
                </>
              )}

              <div className="rounded-lg border border-[#E8E8E8] bg-[#F9F9F9] overflow-hidden">
                <img
                  src={enlargedOutput.image_url}
                  alt={enlargedOutput.name}
                  className="w-full max-h-[65vh] object-contain mx-auto"
                />
              </div>

              {outputs.length > 1 && (
                <p className="mt-4 text-center text-[14px] text-[#626262]">
                  {enlargedOutputIndex + 1} / {outputs.length}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCategoryOverview;
